import React, { useState } from "react";
import PropTypes from "prop-types";
import { productUrl } from "views_shared/app/javascript/utils/urls";
import SavedGroupsBookmark from "../../source_directory/SavedGroupsBookmark";
import { SourceBadge } from "../../source_directory/SourceBadges";
import HighlightAttribute, { highlightValue } from "../../source_directory/HighlightAttribute";
import RequestSampleButton from "../RequestSampleButton";
import launchesLabel from "./launchesLabel";
import ProductImagePlaceholder, { isPlaceholderProductImage } from "../reusable/product_image_placeholder";
import CardEmptyBody from "../../source_directory/CardEmptyBody";
import BodyTextWithExpand from "../../feeds/BodyTextWithExpand";

function ProductCardGrid({ hit }) {
  const [imgError, setImgError] = useState(false);
  const productPath = productUrl({ slug: hit.slug, migrate_id: hit.migrate_id });
  const company = hit.company?.name;
  const category = hit.product_categories?.[0]?.name;
  const hasRealImage = !isPlaceholderProductImage(hit.profile_image_url) && !imgError;
  const priceLine = [hit.price > 0 ? `$${hit.price.toLocaleString()} USD` : null, launchesLabel(hit.released_at)]
    .filter(Boolean)
    .join(" · ");

  return (
    <div
      className="animated faster fadeIn"
      style={{
        gridRow: "span 3",
        display: "grid",
        gridTemplateRows: "subgrid",
        borderRadius: 10,
        border: "1px solid #e5e7eb",
        overflow: "hidden",
        backgroundColor: "#fff",
      }}
    >
      {/* Row 1: Image — a fixed-height white band with a hairline divider under it, the photo
          CONTAINED (not cropped) inside. A product with no image of its own shows the gray
          rounded-tile placeholder inset in the band. */}
      <div
        style={{
          position: "relative",
          width: "100%",
          height: 220,
          background: "#FFFFFF",
          borderBottom: "1px solid #e5e7eb",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {hasRealImage ? (
          <img
            src={hit.profile_image_url}
            alt={hit.name}
            onError={() => setImgError(true)}
            style={{ maxWidth: "82%", maxHeight: "87%", objectFit: "contain" }}
          />
        ) : (
          <div style={{ width: 192, height: 192 }}>
            <ProductImagePlaceholder iconFontSize="3rem" />
          </div>
        )}
        <div
          style={{
            position: "absolute",
            top: 8,
            right: 8,
            background: "rgba(255,255,255,0.8)",
            borderRadius: 6,
            padding: 2,
          }}
        >
          <SavedGroupsBookmark
            sourceableType="Id::PromoEntity::Product"
            sourceableId={hit.id}
            sourceMeta={{ name: hit.name, imageUrl: hit.profile_image_url, slug: hit.slug }}
          />
        </div>
      </div>

      {/* Row 2: Content */}
      <div style={{ paddingLeft: 16, paddingRight: 16, paddingBottom: 16 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 8,
            marginBottom: 4,
          }}
        >
          <div style={{ fontSize: 12, color: "#6b7280" }}>
            {company && <HighlightAttribute hit={hit} attribute={["company", "name"]} fallback={company} />}
            {company && category ? " · " : ""}
            {category}
          </div>
          <RequestSampleButton
            productId={hit.id}
            productName={hit.name}
            samplesAvailable={hit.samples_available}
            variant="link"
          />
        </div>
        <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>
          <a href={productPath} style={{ color: "#111827" }}>
            <HighlightAttribute hit={hit} attribute="name" fallback={hit.name} />
          </a>
        </div>
        {(priceLine || hit.samples_available) && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            {priceLine && <span style={{ fontSize: 14, fontWeight: 500, color: "#374151" }}>{priceLine}</span>}
            {hit.samples_available && <SourceBadge type="sample_available" />}
          </div>
        )}
        {hit.description ? (
          <BodyTextWithExpand
            body={hit.description}
            maxLength={230}
            highlightValue={highlightValue(hit, "description")}
            textClassName=""
            textStyle={{ fontSize: 13, color: "#6b7280", marginBottom: 0 }}
            toggleClassName="paragraph-xs"
          />
        ) : (
          <CardEmptyBody message="No product description, yet." />
        )}
      </div>

      {/* Row 3: CTA */}
      <div style={{ padding: "0 16px 16px" }}>
        <a
          href={productPath}
          className="btn btn-primary w-100"
          style={{ background: "#4f46e5", border: "none", borderRadius: 8 }}
        >
          View Product
        </a>
      </div>
    </div>
  );
}

ProductCardGrid.propTypes = {
  hit: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    slug: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    profile_image_url: PropTypes.string,
    price: PropTypes.number,
    released_at: PropTypes.string,
    samples_available: PropTypes.bool,
    description: PropTypes.string,
    company: PropTypes.shape({ name: PropTypes.string }),
    product_categories: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string })),
  }).isRequired,
};

export default ProductCardGrid;
